/**
 * SoleadLab 2.0 - SRT Flight Path Parser
 *
 * Parses DJI SRT files to extract GPS flight path data.
 * SRT files contain subtitle-like entries with GPS coordinates embedded.
 *
 * Example SRT format:
 * 1
 * 00:00:00,000 --> 00:00:00,033
 * <font size="28">GPS(32.0705,34.7845,50) ... </font>
 */

export interface FlightPoint {
  timestamp: string;
  coordinates: [number, number, number]; // [lng, lat, altitude]
  startMs: number;
  endMs: number;
}

export interface FlightPathData {
  name: string;
  points: FlightPoint[];
  coordinates: [number, number, number][];
  timestamps: string[];
}

/**
 * Parse time string to milliseconds
 * Format: "HH:MM:SS,mmm"
 */
function parseTimeToMs(timeStr: string): number {
  const match = timeStr.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
  if (!match) return 0;

  const [, hours, minutes, seconds, ms] = match;
  return (
    parseInt(hours) * 3600000 +
    parseInt(minutes) * 60000 +
    parseInt(seconds) * 1000 +
    parseInt(ms)
  );
}

/**
 * Extract GPS coordinates from SRT line
 * Looks for patterns like: GPS(lat,lng,alt) or [latitude: 32.07] [longitude: 34.78]
 */
function extractGpsFromLine(line: string): [number, number, number] | null {
  // DJI format: GPS(lat,lng,alt)
  const gpsMatch = line.match(/GPS\s*\(\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*\)/i);
  if (gpsMatch) {
    const lat = parseFloat(gpsMatch[1]);
    const lng = parseFloat(gpsMatch[2]);
    const alt = parseFloat(gpsMatch[3]);
    return [lng, lat, alt]; // Return as [lng, lat, alt] for GeoJSON
  }

  // Alternative format: [latitude: 32.07] [longitude: 34.78] [altitude: 50]
  const latMatch = line.match(/\[?\s*latitude\s*:\s*(-?\d+\.?\d*)\s*\]?/i);
  const lngMatch = line.match(/\[?\s*longitude\s*:\s*(-?\d+\.?\d*)\s*\]?/i);
  const altMatch = line.match(/\[?\s*(?:altitude|alt)\s*:\s*(-?\d+\.?\d*)\s*\]?/i);

  if (latMatch && lngMatch) {
    const lat = parseFloat(latMatch[1]);
    const lng = parseFloat(lngMatch[1]);
    const alt = altMatch ? parseFloat(altMatch[1]) : 0;
    return [lng, lat, alt];
  }

  return null;
}

/**
 * Parse SRT file content into flight path data
 */
export function parseSrt(content: string, filename: string): FlightPathData {
  const lines = content.split('\n');
  const points: FlightPoint[] = [];

  let i = 0;
  while (i < lines.length) {
    // Skip empty lines
    if (!lines[i].trim()) {
      i++;
      continue;
    }

    // Try to read a subtitle block
    // Line 1: Index number
    const indexLine = lines[i].trim();
    if (!/^\d+$/.test(indexLine)) {
      i++;
      continue;
    }

    i++;
    if (i >= lines.length) break;

    // Line 2: Timestamp (00:00:00,000 --> 00:00:00,033)
    const timeLine = lines[i].trim();
    const timeMatch = timeLine.match(/(\d{2}:\d{2}:\d{2},\d{3})\s*-->\s*(\d{2}:\d{2}:\d{2},\d{3})/);
    if (!timeMatch) {
      i++;
      continue;
    }

    const startMs = parseTimeToMs(timeMatch[1]);
    const endMs = parseTimeToMs(timeMatch[2]);

    i++;
    if (i >= lines.length) break;

    // Line 3+: Content with GPS data
    let contentLines = '';
    while (i < lines.length && lines[i].trim() !== '' && !/^\d+$/.test(lines[i].trim())) {
      contentLines += lines[i] + ' ';
      i++;
    }

    // Extract GPS from content
    const coords = extractGpsFromLine(contentLines);
    if (coords) {
      points.push({
        timestamp: timeMatch[1],
        coordinates: coords,
        startMs,
        endMs,
      });
    }
  }

  // Extract just coordinates and timestamps for the flight path
  const coordinates = points.map((p) => p.coordinates);
  const timestamps = points.map((p) => p.timestamp);

  return {
    name: filename.replace(/\.[^.]+$/, ''),
    points,
    coordinates,
    timestamps,
  };
}

/**
 * Parse SRT file
 */
export async function parseSrtFile(file: File): Promise<FlightPathData> {
  const content = await file.text();
  return parseSrt(content, file.name);
}

/**
 * Interpolate flight path position at a given timestamp (milliseconds).
 * Useful for correlating photo EXIF timestamps to flight path positions.
 */
export function interpolatePosition(
  points: FlightPoint[],
  targetMs: number,
): [number, number, number] | null {
  if (points.length === 0) return null;
  if (targetMs <= points[0].startMs) return points[0].coordinates;
  if (targetMs >= points[points.length - 1].startMs) return points[points.length - 1].coordinates;

  // Binary search for the surrounding points
  let lo = 0, hi = points.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid].startMs <= targetMs) lo = mid;
    else hi = mid;
  }

  const p0 = points[lo];
  const p1 = points[hi];
  const span = p1.startMs - p0.startMs;
  if (span <= 0) return p0.coordinates;

  const t = (targetMs - p0.startMs) / span;
  return [
    p0.coordinates[0] + t * (p1.coordinates[0] - p0.coordinates[0]),
    p0.coordinates[1] + t * (p1.coordinates[1] - p0.coordinates[1]),
    p0.coordinates[2] + t * (p1.coordinates[2] - p0.coordinates[2]),
  ];
}
